import React, { useMemo } from 'react';
import { View, Text, StyleSheet, FlatList, Dimensions } from 'react-native';

const TournamentProjectorScreen = ({ route }) => {
  const { tournament } = route.params || {};
  const participants = tournament?.participants?.items || [];
  const standings =
    tournament?.standings?.items
      ?.filter(Boolean)
      ?.sort((a, b) => (a.rank || 999) - (b.rank || 999)) || [];

  const formatSettings = useMemo(() => {
    if (!tournament?.formatConfig) return {};
    try {
      return JSON.parse(tournament.formatConfig) || {};
    } catch (error) {
      console.warn('Invalid formatConfig JSON', error);
      return {};
    }
  }, [tournament]);

  const announcements = formatSettings.announcements || [];

  const derivedStandings = standings.length
    ? standings
    : participants
        .map((participant) => ({
          id: participant.id,
          rank: 0,
          score: participant.score || 0,
          participant,
        }))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return (a.participant?.seedNumber || 0) - (b.participant?.seedNumber || 0);
        })
        .map((row, idx) => ({ ...row, rank: idx + 1 }));

  const pairings = tournament?.pairings?.items || [];

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{tournament?.name || 'Tournament'}</Text>
      <Text style={styles.subtitle}>
        Round {tournament?.currentRound || 0}/{tournament?.totalRounds || '?'} •{' '}
        {tournament?.type?.replace('_', ' ')}
      </Text>
      <View style={styles.columns}>
        <View style={styles.column}>
          <Text style={styles.columnTitle}>Standings</Text>
          <FlatList
            data={derivedStandings}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => (
              <View style={styles.row}>
                <Text style={styles.rank}>{item.rank || '-'}</Text>
                <Text style={styles.name}>{item.participant?.guestName || 'Player'}</Text>
                <Text style={styles.score}>{item.score?.toFixed(1) || '0.0'}</Text>
              </View>
            )}
          />
        </View>
        <View style={styles.column}>
          <Text style={styles.columnTitle}>Pairings</Text>
          <FlatList
            data={pairings}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => (
              <View style={styles.row}>
                <Text style={styles.rank}>B{item.boardNumber}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={styles.name}>
                    {item.whiteParticipant?.guestName || 'White'}
                  </Text>
                  <Text style={styles.name}>
                    {item.blackParticipant?.guestName || 'Black'}
                  </Text>
                </View>
              </View>
            )}
          />
        </View>
      </View>
      {announcements.length > 0 && (
        <View style={styles.announcementBar}>
          <Text style={styles.announcementText}>
            {announcements
              .slice(0, 3)
              .map((ann) => ann.message)
              .join('   •   ')}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0D1117',
    padding: 24,
  },
  title: {
    fontSize: 40,
    fontWeight: 'bold',
    color: 'white',
  },
  subtitle: {
    fontSize: 20,
    color: '#9CA3AF',
    marginBottom: 24,
  },
  columns: {
    flexDirection: 'row',
    gap: 24,
    flex: 1,
  },
  column: {
    flex: 1,
    backgroundColor: '#161B22',
    borderRadius: 16,
    padding: 16,
    height: Dimensions.get('window').height - 150,
  },
  columnTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 12,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  rank: {
    width: 50,
    color: '#94EB98',
    fontWeight: '700',
    fontSize: 18,
  },
  name: {
    flex: 1,
    color: 'white',
    fontSize: 16,
  },
  score: {
    width: 60,
    textAlign: 'right',
    color: '#FBBF24',
    fontWeight: '700',
  },
  announcementBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#111827',
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  announcementText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
});

export default TournamentProjectorScreen;

